import { useCaptchaStore } from '@/stores/captcha';
import { storeToRefs } from 'pinia';
import { watch, computed } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3';

export function useCaptcha() {
  const captchaStore = useCaptchaStore();
  const { turnstileToken, widgetId } = storeToRefs(captchaStore);
  const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

  const captchaProvider = computed(() => import.meta.env.VITE_APP_CAPTCHA_PROVIDER || 'recaptcha');
  const isTurnstile = computed(() => captchaProvider.value === 'turnstile');

  const getCaptchaToken = async (action) => {
    if (isTurnstile.value) {
      return getTurnstileToken();
    } else {
      return getRecaptchaToken(action);
    }
  };

  const getTurnstileToken = async () => {
    captchaStore.resetToken();

    if (!widgetId.value) {
      const turnstileElement = document.querySelector('iframe[src^="https://challenges.cloudflare.com"]');
      if (turnstileElement) {
        widgetId.value = turnstileElement.dataset.widgetId;
        captchaStore.setWidgetId(widgetId.value);
      } else {
        console.error('Turnstile widget not found in the DOM');
        return null;
      }
    }

    return new Promise((resolve) => {
      const executeToken = () => {
        try {
          window.turnstile.reset(widgetId.value);
          window.turnstile.execute(widgetId.value);
        } catch (error) {
          console.error('Error executing Turnstile:', error);
          resolve(null);
        }
      };

      if (window.turnstile) {
        executeToken();
      } else {
        window.addEventListener('turnstile-loaded', executeToken, { once: true });
      }

      const stopWatch = watch(turnstileToken, (newToken) => {
        if (newToken) {
          resolve(newToken);
          stopWatch();
        }
      });
    });
  };

  const getRecaptchaToken = async (action) => {
    try {
      await recaptchaLoaded();
      return await executeRecaptcha(action);
    } catch (error) {
      console.error('Error executing reCAPTCHA:', error);
      return null;
    }
  };

  return { getCaptchaToken, isTurnstile };
}